import React, { useState, useEffect } from 'react';
import { Check, Loader, AlertCircle, ChevronRight, ChevronDown, Brain } from 'lucide-react';
import { TOOL_ICONS, TOOL_COLORS, toolDisplayName } from '../utils/toolConstants';
import { useLanguage } from '../i18n';

const ThinkingStepRow = ({ step, compact }) => {
    const { t, language } = useLanguage();
    const [thinkingExpanded, setThinkingExpanded] = useState(false);
    const colors = TOOL_COLORS[step.tool] || TOOL_COLORS.__status__;
    // The live per-turn status label ("Searching documents", "Processing"). Resolved
    // through toolDisplayName so it follows the language; it falls back Spanish →
    // English → the raw id, so a backend tool the FE has not registered still reads
    // as something rather than blank.
    const displayName = toolDisplayName(step.tool, language);
    const isRunning = step.status === 'running';
    const isError = step.resultSummary?.startsWith('Error');
    const isThinking = step.tool === 'thinking';

    if (isThinking) {
        return (
            <div className={compact ? '' : 'animate-fade-slide-in'}>
                <button
                    onClick={() => setThinkingExpanded(!thinkingExpanded)}
                    className="flex items-center gap-1.5 text-sm text-pai-amber hover:brightness-125 transition-colors"
                >
                    {isRunning ? (
                        <Loader className="h-3.5 w-3.5 animate-spin" />
                    ) : thinkingExpanded ? (
                        <ChevronDown className="h-3.5 w-3.5" />
                    ) : (
                        <ChevronRight className="h-3.5 w-3.5" />
                    )}
                    <Brain className="h-3.5 w-3.5" />
                    <span className="font-medium">{t('msgThinkingLabel', 'Thinking')}</span>
                </button>
                {thinkingExpanded && step.argsSummary && (
                    <div className="mt-1 ml-[22px] text-xs text-pai-meta bg-[rgba(245,165,36,0.06)] border border-[rgba(245,165,36,0.28)] rounded p-2 whitespace-pre-wrap max-h-48 overflow-y-auto">
                        {step.argsSummary}
                    </div>
                )}
            </div>
        );
    }

    return (
        <div
            data-testid="thinking-step-tool"
            data-tool={step.tool}
            className={`flex items-start gap-2 text-sm ${compact ? '' : 'animate-fade-slide-in'}`}
        >
            {/* Icon */}
            <div className={`mt-0.5 shrink-0 ${colors.text}`}>
                {isRunning ? (
                    <Loader className="h-3.5 w-3.5 animate-spin" />
                ) : isError ? (
                    <AlertCircle className="h-3.5 w-3.5 text-red-500" />
                ) : (
                    <Check className="h-3.5 w-3.5 text-green-500" />
                )}
            </div>

            {/* Content */}
            <div className="min-w-0 flex-1">
                <div className="flex items-center gap-1.5 flex-wrap">
                    <span className={`font-medium ${colors.text}`}>{displayName}</span>
                    {step.argsSummary && !isThinking && (
                        <span className="text-pai-meta truncate min-w-0 max-w-full">
                            {step.argsSummary}
                        </span>
                    )}
                    {!isRunning && step.durationMs > 0 && (
                        <span className="text-xs text-pai-faint tabular-nums">
                            {step.durationMs >= 1000
                                ? `${(step.durationMs / 1000).toFixed(1)}s`
                                : `${step.durationMs}ms`}
                        </span>
                    )}
                </div>
                {!isRunning && step.resultSummary && (
                    <div className={`text-xs mt-0.5 ${isError ? 'text-red-500' : 'text-pai-meta'}`}>
                        {step.resultSummary}
                    </div>
                )}
            </div>
        </div>
    );
};

const ThinkingSteps = ({ steps, collapsed = false, totalMs = null, startMs = null, isProcessing = false }) => {
    const { t } = useLanguage();
    const [isExpanded, setIsExpanded] = useState(false);
    // #240 Main Chat Timer: while a turn is still processing, the collapsed header's
    // total-time figure ticks live off the send timestamp (now − startMs). When the
    // turn COMPLETES, `isProcessing` flips false and the header shows the frozen
    // `totalMs` (wall-clock Send→completion). The interval only runs on the
    // collapsed header instance that is actively processing.
    //
    // #328 moved that freeze point: text now arrives while the turn is still
    // running (a lead-in before a tool call), so "first token" no longer means
    // "PAI has finished" and freezing there would show a number that stops
    // several seconds early.
    const ticking = collapsed && isProcessing && !!startMs;
    const [nowMs, setNowMs] = useState(() => Date.now());
    useEffect(() => {
        if (!ticking) return undefined;
        setNowMs(Date.now());
        const id = setInterval(() => setNowMs(Date.now()), 100);
        return () => clearInterval(id);
    }, [ticking, startMs]);

    if (!steps || steps.length === 0) {
        if (collapsed) return null;
        return (
            // Incremental text (#328): the live (non-collapsed) view IS the
            // assistant's "generating" state — it is what the bubble shows from
            // Send until the first content token replaces it. Tagged so the
            // contract "the user never stares at an empty bubble" is assertable.
            <div
                data-testid="assistant-generating"
                className="flex items-center gap-2 text-sm text-pai-meta"
            >
                <Loader className="h-3.5 w-3.5 animate-spin text-blue-500" />
                <span>{t('msgAnalyzingRequest', 'Analyzing your request...')}</span>
            </div>
        );
    }

    // Collapsed mode: compact summary that expands on click
    if (collapsed) {
        const toolSteps = steps.filter(s => s.tool !== 'thinking' && s.tool !== '__status__');
        const thinkingSteps = steps.filter(s => s.tool === 'thinking');
        const toolCount = toolSteps.length;
        const totalDuration = steps.reduce((sum, s) => sum + (s.durationMs || 0), 0);
        const uniqueTools = [...new Set(toolSteps.map(s => s.tool))];

        const summaryParts = [];
        if (toolCount > 0) summaryParts.push(t('msgToolsUsedLabel', '{n} tool{s} used', { n: toolCount, s: toolCount > 1 ? 's' : '' }));
        if (thinkingSteps.length > 0) summaryParts.push(t('msgWithReasoning', 'with reasoning'));
        const summary = summaryParts.join(' ') || t('msgProcessed', 'Processed');

        // #240: the total elapsed time in parens. While processing → live wall-clock
        // (now − startMs); once the turn completes / on reload → the frozen
        // wall-clock total (Send→completion). Legacy messages without a persisted
        // total fall back to the summed per-step durations. Always seconds, 1 decimal.
        let displayMs = null;
        if (ticking) displayMs = nowMs - startMs;
        else if (totalMs != null) displayMs = totalMs;
        else if (totalDuration > 0) displayMs = totalDuration;

        return (
            <div className="mb-3">
                <button
                    onClick={() => setIsExpanded(!isExpanded)}
                    className="flex flex-wrap items-center gap-1.5 max-w-full text-left text-xs text-pai-meta hover:text-white transition-colors group"
                >
                    {isExpanded ? (
                        <ChevronDown className="h-3 w-3" />
                    ) : (
                        <ChevronRight className="h-3 w-3" />
                    )}
                    <span>{summary}</span>
                    {displayMs != null && displayMs > 0 && (
                        <span
                            data-testid="thinking-total-time"
                            className="text-pai-faint tabular-nums"
                        >
                            ({(displayMs / 1000).toFixed(1)}s)
                        </span>
                    )}
                    {/* Tool icons */}
                    <span className="flex items-center gap-0.5 ml-1">
                        {uniqueTools.slice(0, 5).map(tool => {
                            const Icon = TOOL_ICONS[tool] || Loader;
                            const color = TOOL_COLORS[tool] || TOOL_COLORS.__status__;
                            return <Icon key={tool} className={`h-3 w-3 ${color.text}`} />;
                        })}
                    </span>
                </button>
                {isExpanded && (
                    <div className="mt-2 ml-1 pl-3 border-l-2 border-pai-subtle space-y-1.5">
                        {steps.map(step => (
                            <ThinkingStepRow key={step.id} step={step} compact={true} />
                        ))}
                    </div>
                )}
            </div>
        );
    }

    // Live mode: show all steps as they stream in (the same "generating" state
    // as the placeholder above, once real steps exist — #328).
    return (
        <div data-testid="assistant-generating" className="space-y-1.5">
            {steps.map(step => (
                <ThinkingStepRow key={step.id} step={step} compact={false} />
            ))}
        </div>
    );
};

export default ThinkingSteps;
